import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Coins, Warning, ArrowsClockwise, MagnifyingGlass, EnvelopeSimple, Spinner, ArrowSquareOut } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import api from "@/lib/api";
import AccountMenu from "@/components/AccountMenu";
import { useT } from "@/lib/i18n";

/**
 * AgingReportPage — every invoiced quote >N days old that hasn't been
 * fully paid. Powered by /api/admin/aging-report. Click any row to jump
 * straight to the quote/invoice. Click "Send weekly reminders" to manually
 * trigger the auto-reminder sweep (also runs every Monday from the worker).
 */
export default function AgingReportPage({ user, onLogout, onLogoutAll }) {
  const navigate = useNavigate();
  const tx = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [thresh, setThresh] = useState(30);
  const [sendingReminders, setSendingReminders] = useState(false);
  const [reminderResult, setReminderResult] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const r = await api.get("/admin/aging-report", { params: { days_overdue: thresh } });
      setData(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Could not load aging report");
    } finally {
      setLoading(false);
    }
  }, [thresh]);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (!data?.items) return [];
    const q = search.trim().toLowerCase();
    if (!q) return data.items;
    return data.items.filter((it) =>
      [it.company_name, it.invoice_number, it.heading_id, it.prepared_by]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [data, search]);

  const sendReminders = async () => {
    if (!window.confirm(`Email payment reminders to every bill-to contact for invoices >${thresh} days overdue?\n\nThrottled — at most one per invoice per ISO week.`)) return;
    setSendingReminders(true); setReminderResult(null);
    try {
      const r = await api.post("/admin/aging-report/send-reminders", null, { params: { min_days: thresh } });
      setReminderResult(r.data);
    } catch (e) {
      setReminderResult({ ok: false, error: e?.response?.data?.detail || e?.message });
    } finally {
      setSendingReminders(false);
    }
  };

  const fmtMoney = (n) =>
    `$${Math.abs(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="min-h-screen bg-white">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/80 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 md:px-8 h-14 flex items-center gap-3">
          <button onClick={() => navigate("/")} className="w-8 h-8 flex items-center justify-center rounded-sm hover:bg-slate-100 text-slate-500" title={tx("action.back", "Back")}>
            <ArrowLeft size={18} weight="bold" />
          </button>
          <Coins size={20} weight="fill" className="text-amber-600" />
          <h1 className="text-base md:text-lg font-black tracking-tighter font-['Chivo']">
            {tx("aging_report.title", "Aging Report")}
          </h1>
          {data && (
            <span className="text-xs text-slate-500 font-mono">
              {filtered.length}/{data.count} · <strong className="text-rose-700">{fmtMoney(data.total_outstanding)}</strong> outstanding
            </span>
          )}
          <button onClick={load} disabled={loading}
            className={`ml-2 w-7 h-7 flex items-center justify-center rounded-sm hover:bg-slate-100 ${loading ? "text-blue-600" : "text-slate-500"}`}
            title={loading ? "Reloading…" : "Reload"} data-testid="aging-reload-btn">
            <ArrowsClockwise size={14} weight="bold" className={loading ? "animate-spin" : ""} />
          </button>
          <div className="ml-auto">
            <AccountMenu user={user} onLogout={onLogout} onLogoutAll={onLogoutAll} />
          </div>
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 pb-3 flex flex-wrap items-center gap-3">
          <div className="relative max-w-md flex-1">
            <MagnifyingGlass size={14} weight="bold" className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="aging-search" placeholder={tx("aging_report.search_placeholder", "Search by company, invoice #…")}
              className="pl-9 h-9 rounded-sm border-slate-300 text-sm" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
          <div className="flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-slate-500">
            {tx("aging_report.days_overdue_label", "Days overdue ≥")}
            <select value={thresh} onChange={(e) => setThresh(parseInt(e.target.value, 10))}
              data-testid="aging-threshold-select"
              className="h-7 px-2 rounded-sm border border-slate-300 bg-white">
              {[7, 14, 30, 45, 60, 90, 120].map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <button onClick={sendReminders} disabled={sendingReminders || !data?.count}
            data-testid="aging-send-reminders-btn"
            className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-sm border border-amber-300 bg-amber-50 text-amber-800 hover:bg-amber-100 text-[11px] font-bold uppercase tracking-wider disabled:opacity-50">
            {sendingReminders ? <Spinner size={12} className="animate-spin" /> : <EnvelopeSimple size={12} weight="fill" />}
            {tx("aging_report.send_reminders", "Send reminders")} ({data?.count || 0})
          </button>
        </div>
        {reminderResult && (
          <div className="max-w-7xl mx-auto px-4 md:px-8 pb-2">
            <div className={`text-[11px] font-medium px-3 py-2 rounded-sm border ${reminderResult.ok ? "bg-emerald-50 text-emerald-800 border-emerald-200" : "bg-rose-50 text-rose-700 border-rose-200"}`}
              data-testid="aging-reminder-result">
              {reminderResult.ok ? (
                <>Sent <strong>{reminderResult.sent}</strong> · skipped <strong>{reminderResult.skipped}</strong> (already-sent or no email) · failed <strong>{reminderResult.failed}</strong></>
              ) : (
                <>{reminderResult.error || "Failed"}</>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="max-w-7xl mx-auto p-4 md:p-8">
        {error && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-sm p-3 mb-3">{error}</div>}
        {loading && !data ? (
          <div className="flex items-center justify-center h-48"><Spinner size={28} className="animate-spin text-slate-400" /></div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-24 text-slate-500" data-testid="aging-empty">
            <Coins size={42} weight="duotone" className="text-emerald-300 mx-auto mb-3" />
            <div className="text-sm font-bold text-emerald-700">{tx("aging_report.all_clear", "All clear — no invoices ≥ {days} days overdue.", { days: thresh })}</div>
          </div>
        ) : (
          <div className="border border-slate-200 rounded-md overflow-hidden bg-white" data-testid="aging-table">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2">{tx("table.company", "Company")}</th>
                  <th className="text-left px-3 py-2">{tx("table.invoice_num", "Invoice #")}</th>
                  <th className="text-left px-3 py-2">{tx("table.issued", "Issued")}</th>
                  <th className="text-right px-3 py-2">{tx("aging_report.col_days_out", "Days Out")}</th>
                  <th className="text-right px-3 py-2">{tx("aging_report.col_total_due", "Total Due")}</th>
                  <th className="text-right px-3 py-2">{tx("table.paid", "Paid")}</th>
                  <th className="text-right px-3 py-2">{tx("table.balance", "Balance")}</th>
                  <th className="text-left px-3 py-2">{tx("aging_report.col_last_pmt", "Last Pmt")}</th>
                  <th className="text-left px-3 py-2">{tx("table.rep", "Rep")}</th>
                  <th />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map((r) => (
                  <tr key={r.heading_id} data-testid={`aging-row-${r.heading_id}`}
                    onClick={() => navigate(`/quote/${encodeURIComponent(r.heading_id)}`)}
                    className="hover:bg-slate-50 cursor-pointer">
                    <td className="px-3 py-2 font-semibold text-slate-900 flex items-center gap-1.5">
                      {r.company_name || r.customer_id || "—"}
                      {r.country === "Canada" && <span className="text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded-sm bg-red-50 text-[#D80621] border border-red-200">CA</span>}
                    </td>
                    <td className="px-3 py-2 font-mono text-[11px]">#{r.invoice_number}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{r.invoice_date}</td>
                    <td className={`px-3 py-2 text-right font-bold font-mono ${r.days_outstanding >= 90 ? "text-rose-700" : r.days_outstanding >= 60 ? "text-orange-600" : "text-amber-600"}`}>
                      {r.days_outstanding}d
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] text-slate-600">{fmtMoney(r.total_due)}</td>
                    <td className="px-3 py-2 text-right font-mono text-[11px] text-emerald-700">{r.paid > 0 ? fmtMoney(r.paid) : "—"}</td>
                    <td className="px-3 py-2 text-right font-bold font-mono text-rose-700">{fmtMoney(r.balance)}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{r.last_payment_at ? new Date(r.last_payment_at).toLocaleDateString() : "—"}</td>
                    <td className="px-3 py-2 text-[11px] text-slate-500">{r.prepared_by || "—"}</td>
                    <td className="px-3 py-2 text-slate-300"><ArrowSquareOut size={12} weight="bold" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
